import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ONE_MIN_MS } from '@/constants/time';
import { useAuthStore } from '@/features/auth/stores/useAuthStore';
import { useMarketStore } from '@/features/markets/stores/useMarketStore';
import { QueryMarket } from '@/features/markets/types/market';
import { HttpClient, buildEndpointUrl } from '@/libs/axios';
import { AxiosConfig } from '@/models/api';

import { GeneralStats, getEmptyGeneralStats } from '../../types/general-stats';

type GetGeneralStatsResponse = GeneralStats;

interface GetGeneralStatsQueryKeyProps {
    market?: QueryMarket;
    userId?: string;
}

export const getGeneralStatsQueryKey = ({ market, userId }: GetGeneralStatsQueryKeyProps = {}) => [
    'getGeneralStats',
    `market:${market?.id}`,
    `${userId}`,
];

export const getGetGeneralStatsEndpoint = () => buildEndpointUrl('SIDEBAR_STATISTICS');

export const apiGetGeneralStats = async (config?: AxiosConfig) =>
    await HttpClient.get<GetGeneralStatsResponse>(getGetGeneralStatsEndpoint(), config);

//

export const useGetGeneralStats = () => {
    const { authUser, authData } = useAuthStore();
    const { market } = useMarketStore();

    const {
        data,
        isLoading: isInitialLoading,
        isFetching,
    } = useQuery({
        queryKey: getGeneralStatsQueryKey({ market, userId: authUser?.id }),
        queryFn: ({ signal }) => apiGetGeneralStats({ signal }),
        refetchInterval: ONE_MIN_MS,
        enabled: !!authData?.accessToken,
    });

    const generalStats = useMemo(() => data ?? getEmptyGeneralStats(), [data]);

    return {
        isLoading: isInitialLoading || isFetching,
        isInitialLoading,
        isFetching,
        generalStats,
    };
};
